import { useState } from 'react';
import { AccountModel, DonViCoSo, KhachHang, KhoHang, Lo, VatTu, ViTriKho } from '@/types/schemas';

export interface SearchFieldState {
  nhaCungCap: KhachHang | null;
  taiKhoanCo: AccountModel | null;
  vatTu: VatTu | null;
  khoHang: KhoHang | null;
  lo: Lo | null;
  viTri: ViTriKho | null;
  donVi: DonViCoSo | null;
}

export interface SearchFieldActions {
  setNhaCungCap: (nhaCungCap: KhachHang | null) => void;
  setTaiKhoanCo: (taiKhoanCo: AccountModel | null) => void;
  setVatTu: (vatTu: VatTu | null) => void;
  setKhoHang: (khoHang: KhoHang | null) => void;
  setLo: (lo: Lo | null) => void;
  setViTri: (viTri: ViTriKho | null) => void;
  setDonVi: (donVi: DonViCoSo | null) => void;
  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<SearchFieldState>) => void;
}

export interface UseSearchFieldStatesReturn {
  state: SearchFieldState;
  actions: SearchFieldActions;
}

const initialState: SearchFieldState = {
  nhaCungCap: null,
  taiKhoanCo: null,
  vatTu: null,
  khoHang: null,
  lo: null,
  viTri: null,
  donVi: null
};

function transformInitialData(initialData?: MuaHangRecord): Partial<SearchFieldState> {
  if (!initialData) return {};

  return {
    nhaCungCap: initialData.ma_kh_data || null,
    taiKhoanCo: initialData.tk_data || null,
    vatTu: initialData.ma_vt_data || null,
    khoHang: initialData.ma_kho_data || null,
    lo: initialData.ma_lo_data || null,
    viTri: initialData.ma_vi_tri_data || null,
    donVi: initialData.ma_unit_data || null
  };
}

/**
 * Hook for managing search field states in the hoa-don-mua-hang-trong-nuoc search dialog
 * Following the pattern from useFormFieldState
 *
 * @param initialData - Optional initial data to populate the state
 * @returns Object with state and actions for search fields
 */
export function useSearchFieldStates(initialData?: MuaHangRecord): UseSearchFieldStatesReturn {
  const [state, setState] = useState<SearchFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: SearchFieldActions = {
    setNhaCungCap: (nhaCungCap: KhachHang | null) => {
      setState(prev => ({
        ...prev,
        nhaCungCap
      }));
    },

    setTaiKhoanCo: (taiKhoanCo: AccountModel | null) => {
      setState(prev => ({
        ...prev,
        taiKhoanCo
      }));
    },

    setVatTu: (vatTu: VatTu | null) => {
      setState(prev => ({
        ...prev,
        vatTu
      }));
    },

    setKhoHang: (khoHang: KhoHang | null) => {
      setState(prev => ({
        ...prev,
        khoHang
      }));
    },

    setLo: (lo: Lo | null) => {
      setState(prev => ({
        ...prev,
        lo
      }));
    },

    setViTri: (viTri: ViTriKho | null) => {
      setState(prev => ({
        ...prev,
        viTri
      }));
    },
    setDonVi: (donVi: DonViCoSo | null) => {
      setState(prev => ({
        ...prev,
        donVi
      }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<SearchFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
